import React, { useRef, useEffect, useState, useCallback } from 'react';
import './App.css';
import { process, CAMERA_CONFIG, updateCameraDimensions, getCameraConstraints } from './helpers'
import audio_DISTANCE from './audios/DISTANCE.mp3';
import audio_CENTER from './audios/CENTER.mp3';
import audio_STRAIGHT from './audios/STRAIGHT.mp3';
import audio_TURN_LEFT from './audios/TURN_LEFT.mp3';
import audio_TURN_RIGHT from './audios/TURN_RIGHT.mp3';
import audio_TILT_DOWN from './audios/TILT_DOWN.mp3';
import audio_EYES_OPEN from './audios/EYES_OPEN.mp3';
import audio_HOLD from './audios/HOLD.mp3';
import {Camera} from "react-camera-pro";
import { useRive, useStateMachineInput, Layout, Fit, Alignment } from 'rive-react';

//import { WebViewBridge, WebViewEvents, createWebViewEvent } from '@rootally/shared';

// Audio feedback management
const AudioManager = {
  currentAudio: null,
  lastPlayedTime: 0,
  minDelay: 2000, // Minimum delay between feedback audios (2 seconds)
  holdAudioPlayed: false,
  audioBasePath: process.env.PUBLIC_URL + '/audios', // Use public URL for assets

  // Preload all audio files
  audioFiles: {},
  
  // Initialize audio files
  init() {
    const audioKeys = {
      'DISTANCE' : audio_DISTANCE,
      'CENTER' : audio_CENTER,
      'STRAIGHT' : audio_STRAIGHT,
      'TURN_LEFT' : audio_TURN_LEFT,
      'TURN_RIGHT' : audio_TURN_RIGHT,
      'TILT_DOWN' : audio_TILT_DOWN,
      'EYES_OPEN' : audio_EYES_OPEN,
      'HOLD' : audio_HOLD
    }

    Object.keys(audioKeys).forEach(key => {
      const audio = new Audio(audioKeys[key]);
      audio.preload = 'auto';
      this.audioFiles[key] = audio;
      
      // Log which audio files are loading
      console.log(`Loading audio file: ${key}.mp3`);
      
      // Add error handler for each audio file
      audio.onerror = (e) => {
        console.error(`Error loading audio file ${key}.mp3:`, e);
      };
    });
  },

  playAudio(key) {
    const now = Date.now();
    console.log('Attempting to play audio for key:', key);
    
    // Don't play if another audio is playing
    if (this.currentAudio && !this.currentAudio.ended) {
      console.log('Skipping audio - another audio is playing');
      return;
    }

    // Check minimum delay between audios
    if (now - this.lastPlayedTime < this.minDelay) {
      console.log('Skipping audio - too soon after last audio');
      return;
    }

    try {
      // If it's a HOLD audio and we've already played it, don't play again
      if (key === 'HOLD' && this.holdAudioPlayed) {
        console.log('Skipping HOLD audio - already played');
        return;
      }

      // Stop any existing audio
      if (this.currentAudio) {
        this.currentAudio.pause();
        this.currentAudio.currentTime = 0;
      }

      // Get the audio file from our preloaded collection
      const audioFile = this.audioFiles[key];
      if (!audioFile) {
        console.warn(`No audio file found for key: ${key}`);
        return;
      }

      // Reset and play the audio
      audioFile.currentTime = 0;
      this.currentAudio = audioFile;
      
      console.log(`Playing audio file: ${key}.mp3`);
      audioFile.play()
        .then(() => {
          this.lastPlayedTime = now;
          if (key === 'HOLD') {
            this.holdAudioPlayed = true;
          }
        })
        .catch(error => {
          console.error(`Error playing audio ${key}.mp3:`, error);
        });
    } catch (error) {
      console.error('Error in playAudio:', error);
    }
  },

  stopAudio() {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
    }
  },

  resetHoldState() {
    this.holdAudioPlayed = false;
  }
};

const LoadingScreen = () => (
  <div className="loading-container">
    <div className="loading-content">
      <div className="loading-spinner" />
      <h2 className="loading-title">Initializing Scanner</h2>
      <p className="loading-message">
        Loading configuration<span className="loading-dots"></span>
      </p>
    </div>
  </div>
);

const App = () => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const guideOverlayRef = useRef(null);
  const leftEyeCanvasRef = useRef(null);
  const rightEyeCanvasRef = useRef(null);
  const objectCanvasRef = useRef(null);
  const faceMeshRef = useRef(null);
  const cameraRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const animationFrameRef = useRef(0);
  const allConditionsMetRef = useRef(false);
  const riveRef = useRef(null);
  
  const [faceDetected, setFaceDetected] = useState(false);
  const [faceSizeOk, setFaceSizeOk] = useState(false);
  const [facePositionOk, setFacePositionOk] = useState(false);
  const [eyesOpen, setEyesOpen] = useState(false);
  const [faceTiltOk, setFaceTiltOk] = useState(false);
  const [eyesInGuideOk, setEyesInGuideOk] = useState(false);
  const [isCountingDown, setIsCountingDown] = useState(false);
  const [countdownTime, setCountdownTime] = useState(3);
  const [eyesCaptured, setEyesCaptured] = useState(false);
  const [capturedLeftEye, setCapturedLeftEye] = useState(null);
  const [capturedRightEye, setCapturedRightEye] = useState(null);
  const [capturedObject, setCapturedObject] = useState(null);
  const [canCapture, setCanCapture] = useState(false);
  const [instructionMessage, setInstructionMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [currentConfigLabel, setCurrentConfigLabel] = useState("");
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [dimensions, setDimensions] = useState({
    width: CAMERA_CONFIG.WIDTH,
    height: CAMERA_CONFIG.HEIGHT
  });
  const [currentRiveAnimation, setCurrentRiveAnimation] = useState(null);

  // Constants from original app.js
  const MIN_FACE_SIZE_RATIO = 0.4;
  const MAX_FACE_SIZE_RATIO = 0.8;
  const CAPTURE_COUNTDOWN_TIME = 3;
  const EYE_ASPECT_RATIO_THRESHOLD = 0.25;
  const MAX_TILT_ANGLE = 15;

  // Eye landmarks from original app.js
  const LEFT_EYE_LANDMARKS = [33, 133, 157, 158, 159, 160, 161, 246];
  const RIGHT_EYE_LANDMARKS = [362, 263, 386, 387, 388, 389, 390, 466];
  const LEFT_EYE_CONTOUR = [33, 133, 160, 144, 145, 153, 154, 155, 157, 158, 159, 160, 161, 163];
  const RIGHT_EYE_CONTOUR = [362, 263, 387, 373, 374, 380, 381, 382, 384, 385, 386, 387, 388, 390];

  // Simple scan configuration
// const scanConfigs = {
//   configs: [
//     {
//       id: "left_eye",
//       name: "Left Eye",
//       position: "front",
//       extractRegion: "left_eye"
//     },
//     {
//       id: "right_eye",
//       name: "Right Eye",
//       position: "front",
//       extractRegion: "right_eye"
//     },
//     {
//       id: "front_face",
//       name: "Front Face",
//       position: "front",
//       extractRegion: null
//     },
//     {
//       id: "left_face",
//       name: "Left Side",
//       position: "left",
//       extractRegion: null
//     },
//     {
//       id: "right_face",
//       name: "Right Side",
//       position: "right",
//       extractRegion: null
//     },
//     {
//       id: "head_down",
//       name: "Head Down",
//       position: "down",
//       extractRegion: null
//     }
//   ]
  
// };
////--------------------------------
// const scanConfigs = {
//   configs: [
//     {
//       id: "left_eye",
//       name: "Left Eye",
//       position: "front",
//       extractRegion: "left_eye"
//     }
//   ]
  
// };


  const assessmentIndex = useRef(0);
  const scanConfigs = useRef(null);
  const currentConfigIndex = useRef(0);
  const currentConfig = useRef(null);

  const stream = useRef(null);
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);
  

  // Helper functions from original app.js
  const calculateDistance = (point1, point2) => {
    return Math.sqrt(
      Math.pow((point2.x - point1.x) * CAMERA_CONFIG.WIDTH, 2) +
      Math.pow((point2.y - point1.y) * CAMERA_CONFIG.HEIGHT, 2)
    );
  };

  const calculateFaceScale = (landmarks) => {
    const leftEye = landmarks[33];
    const rightEye = landmarks[263];
    const eyeDistance = Math.sqrt(
      Math.pow((rightEye.x - leftEye.x) * CAMERA_CONFIG.WIDTH, 2) +
      Math.pow((rightEye.y - leftEye.y) * CAMERA_CONFIG.HEIGHT, 2)
    );
    return eyeDistance;
  };

  const calculateEyeCenter = (landmarks, eyeLandmarks) => {
    let sumX = 0, sumY = 0;
    eyeLandmarks.forEach(index => {
      sumX += landmarks[index].x * CAMERA_CONFIG.WIDTH;
      sumY += landmarks[index].y * CAMERA_CONFIG.HEIGHT;
    });
    return {
      x: sumX / eyeLandmarks.length,
      y: sumY / eyeLandmarks.length
    };
  };

  const isFaceWithinGuide = (landmarks) => {
    const centerX = CAMERA_CONFIG.WIDTH / 2;
    const centerY = CAMERA_CONFIG.HEIGHT / 2;
    const radiusX = CAMERA_CONFIG.WIDTH * 0.35;
    const radiusY = CAMERA_CONFIG.HEIGHT * 0.45;
    
    const nose = landmarks[1];
    const noseX = nose.x * CAMERA_CONFIG.WIDTH;
    const noseY = nose.y * CAMERA_CONFIG.HEIGHT;
    
    const normalizedX = (noseX - centerX) / radiusX;
    const normalizedY = (noseY - centerY) / radiusY;
    
    return (Math.pow(normalizedX, 2) + Math.pow(normalizedY, 2)) <= 1.1;
  };

  const calculateFaceTilt = (landmarks) => {
    const leftEye = landmarks[33];
    const rightEye = landmarks[263];
    
    const deltaY = (rightEye.y - leftEye.y) * CAMERA_CONFIG.HEIGHT;
    const deltaX = (rightEye.x - leftEye.x) * CAMERA_CONFIG.WIDTH;
    const angleInDegrees = Math.abs(Math.atan2(deltaY, deltaX) * (180 / Math.PI));
    
    return angleInDegrees;
  };

  const isPointWithinGuide = (x, y) => {
    const centerX = CAMERA_CONFIG.WIDTH / 2;
    const centerY = CAMERA_CONFIG.HEIGHT / 2;
    const radiusX = CAMERA_CONFIG.WIDTH * 0.35;
    const radiusY = CAMERA_CONFIG.HEIGHT * 0.45;
    
    const normalizedX = (x - centerX) / radiusX;
    const normalizedY = (y - centerY) / radiusY;
    
    return (Math.pow(normalizedX, 2) + Math.pow(normalizedY, 2)) <= 1;
  };

  const areEyesWithinGuide = (landmarks) => {
    const leftEyeCenter = calculateEyeCenter(landmarks, LEFT_EYE_LANDMARKS);
    const rightEyeCenter = calculateEyeCenter(landmarks, RIGHT_EYE_LANDMARKS);
    
    return isPointWithinGuide(leftEyeCenter.x, leftEyeCenter.y) && 
           isPointWithinGuide(rightEyeCenter.x, rightEyeCenter.y);
  };

  const calculateEyeAspectRatio = (landmarks, eyeContour) => {
    const p2_p6 = calculateDistance(landmarks[eyeContour[1]], landmarks[eyeContour[5]]);
    const p3_p5 = calculateDistance(landmarks[eyeContour[2]], landmarks[eyeContour[4]]);
    const p1_p4 = calculateDistance(landmarks[eyeContour[0]], landmarks[eyeContour[3]]);
    
    return (p2_p6 + p3_p5) / (2.0 * p1_p4);
  };

  const calculateEyeOpenRatio = (landmarks, eyePoints) => {
    const leftPoint = landmarks[eyePoints[0]];
    const rightPoint = landmarks[eyePoints[1]];
    const upperPoint = landmarks[eyePoints[2]];
    const lowerPoint = landmarks[eyePoints[3]];
    
    const horizontalDist = calculateDistance(leftPoint, rightPoint);
    const verticalDist = calculateDistance(upperPoint, lowerPoint);
    
    return verticalDist / horizontalDist;
  };

  const areEyesFullyOpen = (landmarks) => {
    const leftEAR = calculateEyeAspectRatio(landmarks, [33, 159, 145, 133, 154, 153]);
    const rightEAR = calculateEyeAspectRatio(landmarks, [362, 386, 374, 263, 381, 380]);
    
    const leftEyeRatio = calculateEyeOpenRatio(landmarks, [33, 133, 159, 145]);
    const rightEyeRatio = calculateEyeOpenRatio(landmarks, [362, 263, 386, 374]);
    
    const leftEyeOpen = leftEyeRatio > 0.3;
    const rightEyeOpen = rightEyeRatio > 0.3;
    
    return leftEyeOpen && rightEyeOpen && leftEAR > EYE_ASPECT_RATIO_THRESHOLD && rightEAR > EYE_ASPECT_RATIO_THRESHOLD;
  };

  const calculateDynamicPadding = (width, height, faceScale) => {
    const basePadding = Math.max(width, height) * 0.2;
    const minPadding = faceScale * 0.05;
    const maxPadding = faceScale * 0.15;
    return Math.min(Math.max(basePadding, minPadding), maxPadding);
  };

  const drawRegionOnCanvas = (image, canvas, region) => {
    if (!canvas) return;
    
    // Set canvas dimensions to match guide rectangle
    canvas.width = 360;
    canvas.height = 432;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Calculate center position for cropping
    const centerX = (image.width - canvas.width) / 2;
    const centerY = (image.height - canvas.height) / 2;

    // Draw cropped region
    ctx.drawImage(
      image,
      centerX, centerY, canvas.width, canvas.height,
      0, 0, canvas.width, canvas.height
    );
  };


  const extractEyeRegion = (image, landmarks, eye, faceScale) => {
    if (!areEyesFullyOpen(landmarks)) {
      console.log('Preventing extraction - Eyes not fully open during extraction');
      return false;
    }

    const canvas = eye === 'left' ? leftEyeCanvasRef.current : rightEyeCanvasRef.current;
    if (!canvas) {
      console.log(`${eye} eye canvas not found`);
      return false;
    }
    
    const ctx = canvas.getContext('2d');
    const eyeContour = eye === 'left' ? LEFT_EYE_CONTOUR : RIGHT_EYE_CONTOUR;

    let minX = Infinity, minY = Infinity;
    let maxX = -Infinity, maxY = -Infinity;

    eyeContour.forEach(index => {
      const point = landmarks[index];
      const x = point.x * CAMERA_CONFIG.WIDTH;
      const y = point.y * CAMERA_CONFIG.HEIGHT;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    });

    const eyeWidth = maxX - minX;
    const eyeHeight = maxY - minY;
    const padding = calculateDynamicPadding(eyeWidth, eyeHeight, faceScale);

    minX = Math.max(0, minX - padding);
    maxX = Math.min(CAMERA_CONFIG.WIDTH, maxX + padding);
    minY = Math.max(0, minY - padding);
    maxY = Math.min(CAMERA_CONFIG.HEIGHT, maxY + padding);

    const width = maxX - minX;
    const height = maxY - minY;

    //console.log(`Extracting ${eye} eye:`, { minX, minY, width, height, faceScale });

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(
      image,
      minX, minY, width, height,
      0, 0, canvas.width, canvas.height
    );
    
    return true;
  };

  const drawGuideOval = (ctx) => {
    const centerX = CAMERA_CONFIG.WIDTH / 2;
    const centerY = CAMERA_CONFIG.HEIGHT / 2;
    const radiusX = CAMERA_CONFIG.WIDTH * 0.35;
    const radiusY = CAMERA_CONFIG.HEIGHT * 0.45;

    ctx.beginPath();
    ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, Math.PI * 2);
    ctx.strokeStyle = '#00ff00';
    ctx.lineWidth = Math.max(2, CAMERA_CONFIG.WIDTH * 0.003);
    ctx.stroke();
  };

  const drawAnimatedEyeCircle = (ctx, centerX, centerY, timestamp, progress, faceScale) => {
    const baseRadius = faceScale * 0.3;
    const animationSpeed = 0.003;
    const pulseAmount = faceScale * 0.03;
    
    const radius = baseRadius + Math.sin(timestamp * animationSpeed) * pulseAmount;
    
    const green = Math.floor(200 * progress);
    const redBlue = Math.floor(80 * (1 - progress));
    
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.strokeStyle = `rgb(${redBlue}, ${green}, ${redBlue})`;
    ctx.lineWidth = Math.max(2, faceScale * 0.03);
    ctx.stroke();
  };


  // Countdown functions
  const startCaptureCountdown = () => {

    // Check if timer already exists FIRST - this prevents multiple timers
    if (countdownTimerRef.current) {
        console.log('Timer already exists, skipping...');
        return;
    }

    if (isCountingDown) return;

    // // Double-check conditions are still valid before starting timer
    // if (!faceSizeOk || !facePositionOk || !faceTiltOk || !eyesInGuideOk || !eyesOpen || !faceDetected) {
    //     console.log('Conditions not met when starting countdown, skipping...', {
    //         faceSizeOk,
    //         facePositionOk,
    //         faceTiltOk,
    //         eyesInGuideOk,
    //         eyesOpen,
    //         faceDetected
    //     });
    //     return;
    // }

    console.log('Starting startCaptureCountdown...');
    
    setIsCountingDown(true);
    setCanCapture(false);
    setCountdownTime(CAPTURE_COUNTDOWN_TIME);

    console.log('isCountingDown - startCaptureCountdown:', isCountingDown);
    console.log('canCapture - startCaptureCountdown:', canCapture);
    console.log('countdownTime - startCaptureCountdown:', countdownTime);
    
    //// Only show countdown message if there's no error message
    // if (!errorMessage.textContent) {
    //     instructionMessage.textContent = `Open your eyes wide and don't move! Capturing in ${Math.ceil(countdownTime)}...`;
    // } else {
    //     instructionMessage.textContent = "";
    // }
    
    countdownTimerRef.current = setInterval(() => {
        console.log("setting countdown time interval function");
        console.log('Timer ID:', countdownTimerRef.current);
        
        // Check if conditions are still valid FIRST - outside the setState callback
        if (!allConditionsMetRef.current) {
            console.log('Cancelling countdown due to:', {
                allConditionsMetRef: allConditionsMetRef.current
            });
            cancelCaptureCountdown("countdownTime interval function");
            return; // This properly exits the interval function
        }
        
        console.log('Conditions still valid, proceeding with countdown...');
        
        setCountdownTime(prev => {
            const newTime = prev - 0.2;
            console.log('newTime:', newTime);
            
            if (newTime > 0) {

                setInstructionMessage(`Keep your eyes open and don't move! Capturing in ${Math.ceil(newTime)}...`);

                return newTime;
            } else {
                  console.log('Countdown timer expired, clearing interval...');
                  clearInterval(countdownTimerRef.current);
                  countdownTimerRef.current = null;
                
                // Final check before capture
                if (allConditionsMetRef.current) {
                    setCanCapture(true);
                    setTimeout(captureEyes, 100);
                } else {
                    console.log('Final check failed:', {
                        allConditionsMetRef: allConditionsMetRef.current
                    });
                    // Don't call cancelCaptureCountdown here since we already cleared the timer
                    setIsCountingDown(false);
                    setCanCapture(false);
                    setCountdownTime(CAPTURE_COUNTDOWN_TIME);
                }
                return 0;
            }
        });
    }, 200);

    
    // if (isCountingDown || eyesCaptured) return;
    
    // console.log('Starting capture countdown...');
    // setIsCountingDown(true);
    // setCountdownTime(CAPTURE_COUNTDOWN_TIME);
    
    // countdownTimerRef.current = setInterval(() => {
    //   // Check if eyes are already captured before continuing countdown
    //   if (eyesCaptured) {
    //     clearInterval(countdownTimerRef.current);
    //     countdownTimerRef.current = null;
    //     return;
    //   }
      
    //   setCountdownTime(prev => {
    //     const newTime = prev - 0.1;
        
    //     if (newTime <= 0) {
    //       clearInterval(countdownTimerRef.current);
    //       countdownTimerRef.current = null;
    //       captureEyes();
    //       return 0;
    //     }
        
    //     return newTime;
    //   });
    // }, 100);
  };

  const cancelCaptureCountdown = (callingFunction) => {
    console.log('Cancelling countdown...', callingFunction);
    console.log('countdownTimerRef.current:', countdownTimerRef.current);
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }

    setIsCountingDown(false);
    setCanCapture(false);
    setCountdownTime(CAPTURE_COUNTDOWN_TIME);
    // Reset the HOLD audio state when countdown is cancelled
    AudioManager.resetHoldState();
  };

  const captureEyes = () => {

    console.log('captureEyes function');
    console.log('canCapture:', canCapture);
    console.log('allConditionsMetRef.current:', allConditionsMetRef.current);
    // Final validation before capture
    if (!allConditionsMetRef.current) {
        console.log('Capture prevented due to:', {
            canCapture: canCapture,
            allConditionsMetRef: allConditionsMetRef.current
            // leftEAR: lastEyeState.leftEAR,
            // rightEAR: lastEyeState.rightEAR
        });
        
        // If eyes are closed, show specific message
        if (!eyesOpen) {
            // errorMessage.textContent = "Don't close your eyes";
            // instructionMessage.textContent = ""; // Clear any existing instruction message
        }
        
        cancelCaptureCountdown("captureEyes function");
        return;
    }

    // Prevent multiple captures
    if (eyesCaptured) {
      console.log('Eyes already captured, skipping...');
      return;
    }
    
    console.log('Capturing eyes...');
    
    // Clear the countdown timer first
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    
    // Set captured state immediately to prevent multiple calls
    setEyesCaptured(true);
    setIsCountingDown(false);
    
    setCurrentConfigLabel(currentConfig.current.name);

    // Capture the current eye images
    if (leftEyeCanvasRef.current) {
      setCapturedLeftEye(leftEyeCanvasRef.current.toDataURL());
    }
    if (rightEyeCanvasRef.current) {
      setCapturedRightEye(rightEyeCanvasRef.current.toDataURL());
    }
    if (objectCanvasRef.current) {
      setCapturedObject(objectCanvasRef.current.toDataURL());
    }
    
    // Stop camera
    if (cameraRef.current) {
      cameraRef.current.stop();
    }
  };

  const waitForMediaPipe = async () => {
    console.log('Waiting for MediaPipe scripts...');
    const maxAttempts = 20;
    const delayMs = 500;
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      console.log(`Checking MediaPipe (${attempt}/${maxAttempts})...`);
      
      if (window.Camera && window.FaceMesh) {
        console.log('✅ MediaPipe scripts ready!');
        return;
      }
      
      if (attempt === maxAttempts) {
        throw new Error('MediaPipe scripts failed to load');
      }
      
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
  };

  const initializeFaceMesh = async () => {
    console.log('=== STEP 3: Initializing FaceMesh ===');
    
    if (!window.FaceMesh) {
      throw new Error('FaceMesh not loaded');
    }

    try {
      const faceMesh = new window.FaceMesh({
        locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`
      });

      faceMesh.setOptions({
        maxNumFaces: 1,
        refineLandmarks: true,
        minDetectionConfidence: 0.5,
        minTrackingConfidence: 0.5
      });

      //faceMesh.onResults(onFaceMeshResults);
      faceMeshRef.current = faceMesh;
      console.log('✅ FaceMesh initialized!');
    } catch (error) {
      console.error('FaceMesh initialization error:', error);
      throw new Error('Failed to initialize face detection');
    }
  };

  const onFaceMeshResults = (results) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const canvasCtx = canvas.getContext('2d');
    canvasCtx.save();
    canvasCtx.clearRect(0, 0, canvas.width, canvas.height);
    canvasCtx.drawImage(results.image, 0, 0, canvas.width, canvas.height);

    // Draw guide oval
    //drawGuideOval(canvasCtx);

    const landmarks = results.multiFaceLandmarks[0];

    const processedResults = process(results, currentConfig.current);
    //console.log('processedResults:', processedResults);

    // Handle feedback message and audio
    if (processedResults.feedback) {
      setFeedbackMessage(processedResults.feedback.message);
      
      // if (processedResults.isValid) {
      //   // Stop any existing feedback audio
      //   AudioManager.stopAudio();
      //   // Play HOLD audio if not already played
      //   AudioManager.playAudio('HOLD');
      // } else {
      //   // Play feedback audio based on the key
      //   console.log("playing audio:", processedResults.feedback.key);
      //   AudioManager.playAudio(processedResults.feedback.key);
      // }
    }

    if (results.multiFaceLandmarks.length > 0) {
      const allConditionsMet = processedResults.isValid;
      allConditionsMetRef.current = allConditionsMet;


      //if (processedResults.isValid) {
        //onFaceMeshResultsOriginal(processedResults);

        

        if (allConditionsMet) {
          console.log('Processed results are valid:', processedResults);
          
          // TODO: Add eye capture logic here
          // const leftEyeCenter = calculateEyeCenter(landmarks, LEFT_EYE_LANDMARKS);
          // const rightEyeCenter = calculateEyeCenter(landmarks, RIGHT_EYE_LANDMARKS);

          // // Extract eye regions
          // extractEyeRegion(results.image, landmarks, 'left', faceScale);
          // extractEyeRegion(results.image, landmarks, 'right', faceScale);

          // if (isCountingDown) {
          //   const progress = (CAPTURE_COUNTDOWN_TIME - countdownTime) / CAPTURE_COUNTDOWN_TIME;
            
          //   // Draw animated circles
          //   drawAnimatedEyeCircle(canvasCtx, leftEyeCenter.x, leftEyeCenter.y, animationFrameRef.current, progress, faceScale);
          //   drawAnimatedEyeCircle(canvasCtx, rightEyeCenter.x, rightEyeCenter.y, animationFrameRef.current, progress, faceScale);

          //   animationFrameRef.current++;
          // }
          //end TODO

          
          const region = processedResults.region;

          drawRegionOnCanvas(results.image, objectCanvasRef.current, region);
          //drawRegionOnCanvas(results.image,rightEyeCanvasRef.current, rightEye);

          
          // setCapturedLeftEye(leftEyeCanvasRef.current.toDataURL());
          // setCapturedRightEye(rightEyeCanvasRef.current.toDataURL());

          // // Get the image data
          // const leftEyeImage = leftEyeCanvas.toDataURL('image/jpeg');
          // const rightEyeImage = rightEyeCanvas.toDataURL('image/jpeg');

          // Here you can handle the captured eye images
          //console.log('Captured eyes:', { leftEyeImage, rightEyeImage });

          if (!isCountingDown && !eyesCaptured) {
          //   console.log('Starting countdown...');
          //   console.log('isCountingDown:', isCountingDown);
          //   console.log('eyesCaptured:', eyesCaptured);
            // Only start if no timer exists
            if (!countdownTimerRef.current) {
              startCaptureCountdown();
            }
          }
        } else {
          // Cancel countdown if conditions not met
          if (isCountingDown && !eyesCaptured) {
            cancelCaptureCountdown("onFaceMeshResults function");
          }
        }

        // Draw face outline for visual feedback
        const faceContour = [
          10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288,
          397, 365, 379, 378, 400, 377, 152, 148, 176, 149, 150, 136,
          172, 58, 132, 93, 234, 127, 162, 21, 54, 103, 67, 109
        ];
        
        canvasCtx.strokeStyle = allConditionsMet ? '#00ff00' : '#ff0000';
        canvasCtx.lineWidth = 2;
        canvasCtx.beginPath();
        
        faceContour.forEach((landmarkIndex, i) => {
          const landmark = landmarks[landmarkIndex];
          const x = landmark.x * canvas.width;
          const y = landmark.y * canvas.height;
          
          if (i === 0) {
            canvasCtx.moveTo(x, y);
          } else {
            canvasCtx.lineTo(x, y);
          }
        });
        
        canvasCtx.closePath();
        canvasCtx.stroke();
    //}
    }else {
      if (isCountingDown && !eyesCaptured) {
        cancelCaptureCountdown("onFaceMeshResults function");
      }
    }


    canvasCtx.restore();
  };

  const onFaceMeshResultsOriginal = (results) => {
    if (eyesCaptured) {
      console.log('Eyes already captured, skipping processing');
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const canvasCtx = canvas.getContext('2d');
    canvasCtx.save();
    canvasCtx.clearRect(0, 0, canvas.width, canvas.height);
    canvasCtx.drawImage(results.image, 0, 0, canvas.width, canvas.height);

    // Draw guide oval
    drawGuideOval(canvasCtx);

    if (results.multiFaceLandmarks && results.multiFaceLandmarks.length > 0) {
      setFaceDetected(true);
      const landmarks = results.multiFaceLandmarks[0];
      
      // Calculate face scale for dynamic sizing
      const faceScale = calculateFaceScale(landmarks);
      
      // Check face size
      const faceWidth = Math.abs(landmarks[234].x - landmarks[454].x);
      const currentFaceSizeOk = faceWidth > MIN_FACE_SIZE_RATIO && faceWidth < MAX_FACE_SIZE_RATIO;
      setFaceSizeOk(currentFaceSizeOk);
      
      // Check face position
      const currentFacePositionOk = isFaceWithinGuide(landmarks);
      setFacePositionOk(currentFacePositionOk);
      
      // Check face tilt
      const tiltAngle = calculateFaceTilt(landmarks);
      const currentFaceTiltOk = tiltAngle <= MAX_TILT_ANGLE;
      setFaceTiltOk(currentFaceTiltOk);
      
      // Check if eyes are within guide
      const currentEyesInGuideOk = areEyesWithinGuide(landmarks);
      setEyesInGuideOk(currentEyesInGuideOk);
      
      // Check if eyes are open
      const currentEyesOpen = areEyesFullyOpen(landmarks);
      setEyesOpen(currentEyesOpen);

      // If countdown is active and conditions fail, cancel immediately
      if (isCountingDown && !eyesCaptured && (!currentFaceSizeOk || !currentFacePositionOk || !currentFaceTiltOk || !currentEyesInGuideOk || !currentEyesOpen)) {
        console.log('Cancelling countdown due to failed conditions');
        cancelCaptureCountdown("onFaceMeshResults function");
      }

      // All conditions check
      const allConditionsMet = currentFaceSizeOk && currentFacePositionOk && currentFaceTiltOk && currentEyesInGuideOk && currentEyesOpen;

      allConditionsMetRef.current = allConditionsMet;

      if (allConditionsMet) {
        
        const leftEyeCenter = calculateEyeCenter(landmarks, LEFT_EYE_LANDMARKS);
        const rightEyeCenter = calculateEyeCenter(landmarks, RIGHT_EYE_LANDMARKS);

        // Extract eye regions
        extractEyeRegion(results.image, landmarks, 'left', faceScale);
        extractEyeRegion(results.image, landmarks, 'right', faceScale);

        if (isCountingDown) {
          const progress = (CAPTURE_COUNTDOWN_TIME - countdownTime) / CAPTURE_COUNTDOWN_TIME;
          
          // Draw animated circles
          drawAnimatedEyeCircle(canvasCtx, leftEyeCenter.x, leftEyeCenter.y, animationFrameRef.current, progress, faceScale);
          drawAnimatedEyeCircle(canvasCtx, rightEyeCenter.x, rightEyeCenter.y, animationFrameRef.current, progress, faceScale);

          animationFrameRef.current++;
        }

        if (!isCountingDown && !eyesCaptured) {
        //   console.log('Starting countdown...');
        //   console.log('isCountingDown:', isCountingDown);
        //   console.log('eyesCaptured:', eyesCaptured);
          // Only start if no timer exists
          if (!countdownTimerRef.current) {
            startCaptureCountdown();
          }
        }
      } else {
        // Cancel countdown if conditions not met
        if (isCountingDown && !eyesCaptured) {
          cancelCaptureCountdown("onFaceMeshResults function");
        }
      }

      // Draw face outline for visual feedback
      const faceContour = [
        10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288,
        397, 365, 379, 378, 400, 377, 152, 148, 176, 149, 150, 136,
        172, 58, 132, 93, 234, 127, 162, 21, 54, 103, 67, 109
      ];
      
      canvasCtx.strokeStyle = allConditionsMet ? '#00ff00' : '#ff0000';
      canvasCtx.lineWidth = 2;
      canvasCtx.beginPath();
      
      faceContour.forEach((landmarkIndex, i) => {
        const landmark = landmarks[landmarkIndex];
        const x = landmark.x * canvas.width;
        const y = landmark.y * canvas.height;
        
        if (i === 0) {
          canvasCtx.moveTo(x, y);
        } else {
          canvasCtx.lineTo(x, y);
        }
      });
      
      canvasCtx.closePath();
      canvasCtx.stroke();
      
    } else {
      setFaceDetected(false);
      if (isCountingDown && !eyesCaptured) {
        cancelCaptureCountdown("onFaceMeshResults function");
      }
    }

    canvasCtx.restore();
  };

  const startCamera = async () => {
    console.log('=== STEP 1: Starting video stream ===');
    
    if (!videoRef.current) {
      console.error('Video element is null!');
      return;
    }

    try {
      // Initialize camera
      //const constraints = await getCameraConstraints();
      stream.current = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 932 },
        },
        audio: false
      });

      console.log('Got stream:', stream.current);
      videoRef.current.srcObject = stream.current;

      CAMERA_CONFIG.WIDTH = stream.current.getVideoTracks()[0].getSettings().width;
      CAMERA_CONFIG.HEIGHT = stream.current.getVideoTracks()[0].getSettings().height;

      console.log('CAMERA_CONFIG.WIDTH:', CAMERA_CONFIG.WIDTH);
      console.log('CAMERA_CONFIG.HEIGHT:', CAMERA_CONFIG.HEIGHT);

      setDimensions({
        width: CAMERA_CONFIG.WIDTH,
        height: CAMERA_CONFIG.HEIGHT
      });

      await videoRef.current.play();
      console.log('✅ Video playing successfully!');
      // if (videoRef.current) {
      //   videoRef.current.srcObject = stream;
      //   videoRef.current.width = CAMERA_CONFIG.WIDTH;
      //   videoRef.current.height = CAMERA_CONFIG.HEIGHT;
      // }

      // // Initialize canvases with correct dimensions
      // if (canvasRef.current) {
      //   canvasRef.current.width = CAMERA_CONFIG.WIDTH;
      //   canvasRef.current.height = CAMERA_CONFIG.HEIGHT;
      // }
      // if (objectCanvasRef.current) {
      //   objectCanvasRef.current.width = CAMERA_CONFIG.WIDTH;
      //   objectCanvasRef.current.height = CAMERA_CONFIG.HEIGHT;
      //}

      // Step 2: Wait for MediaPipe
      console.log('=== STEP 2: Waiting for MediaPipe ===');
      await waitForMediaPipe();
      
      // Step 3: Initialize FaceMesh
      await initializeFaceMesh();
      
      // Step 4: Initialize MediaPipe Camera
      console.log('=== STEP 4: Initializing MediaPipe Camera ===');
      console.log('videoRef.current before MediaPipe:', videoRef.current);
      
      console.log('CAMERA_CONFIG.WIDTH:', CAMERA_CONFIG.WIDTH);
      console.log('CAMERA_CONFIG.HEIGHT:', CAMERA_CONFIG.HEIGHT);
      //console.log('constraints:', constraints);
      const camera = new window.Camera(videoRef.current, {
        onFrame: async () => {
          if (faceMeshRef.current && videoRef.current && videoRef.current.readyState === 4) {
            try {
              await faceMeshRef.current.send({ image: videoRef.current });
            } catch (error) {
              console.warn('FaceMesh send error:', error);
            }
          }
        },
      });

      cameraRef.current = camera;
      await camera.start();
      console.log('✅ MediaPipe Camera started!');

    } catch (error) {
      console.error('Camera error:', error);
    }
  };

  const openBase64InNewTab = useCallback((data, mimeType) => {
    // Remove data URL prefix if it exists
    const base64Data = data.includes('base64,') 
      ? data.split('base64,')[1] 
      : data;

    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
  
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
  
    const byteArray = new Uint8Array(byteNumbers);
  
    const file = new Blob([byteArray], {
      type: mimeType + ";base64"
    });
  
    const fileURL = URL.createObjectURL(file);
  
    window.open(fileURL, "_blank");
  }, []);

  const updateZoomFactor = () => {

    const videoClass = document.querySelector("video");
    if (videoClass) {
      if(currentConfig.current.zoomFactor > 1 && !videoClass.classList.contains("zoomed")){
        videoClass.classList.add("zoomed");
      }else if(currentConfig.current.zoomFactor === 1 && videoClass.classList.contains("zoomed")){
        videoClass.classList.remove("zoomed");
      }
    }
    
  }

  const moveToNextConfig = () => {
    
    //save the captured object to the config
    scanConfigs.current[assessmentIndex.current].configs[currentConfigIndex.current].data = capturedObject;

    console.log('capturedObject:', capturedObject);

    currentConfigIndex.current++;
    console.log('assessmentIndex:', assessmentIndex.current);
    console.log('assessment:', scanConfigs.current[assessmentIndex.current].id);
    console.log('currentConfigIndex:', currentConfigIndex.current);
    console.log('scanConfigs.configs.length:', scanConfigs.current[assessmentIndex.current].configs.length);
    
    if (currentConfigIndex.current < scanConfigs.current[assessmentIndex.current].configs.length) {
      currentConfig.current = scanConfigs.current[assessmentIndex.current].configs[currentConfigIndex.current];
      console.log('currentConfig:', currentConfig.current);
      
      // Update position input for new config
      console.log('Updating position for new config');
      triggerPosition(currentConfigIndex.current);
      
      setFeedbackMessage(currentConfig.current.manualInstruction);
      updateZoomFactor();

      return true;
    } else {
      assessmentIndex.current++;
      currentConfigIndex.current = 0;
      if (assessmentIndex.current < scanConfigs.current.length) {
        currentConfig.current = scanConfigs.current[assessmentIndex.current].configs[currentConfigIndex.current];
        console.log('currentConfig:', currentConfig.current);

        updateZoomFactor();

        return true;
      } else {
        console.log('No more configs');
        return false;
      }
    }
  };

  //data: {"type":"SCAN_CONFIG","scanConfigs":[{"id":"as_eys_01","endpoint":"/eye-scan","configs":[{"extractRegion":"left_eye","position":"front","id":"left_eye","name":"Left Eye","zoomed":false},{"extractRegion":"right_eye","position":"front","id":"right_eye","name":"Right Eye","zoomed":false}]}]}
  // First useEffect for message handling and configuration
  useEffect(() => {
    const handleMessage = (event) => {
      console.log('handleMessage', event);
      console.log('event.type:', event.data.type);
      console.log('event.scanConfigs:', event.data.scanConfigs);
      if (event.data.type === 'SCAN_CONFIG') {
       try{
        console.log('type is SCAN_CONFIG');
        scanConfigs.current = event.data.scanConfigs;

        assessmentIndex.current = 0;
        const currentAssessment = scanConfigs.current[assessmentIndex.current];
        currentConfigIndex.current = 0;
        currentConfig.current = currentAssessment.configs[currentConfigIndex.current];
        
        // Load Rive animation if URL is provided
        if (currentAssessment.riveUrl) {
          setCurrentRiveAnimation(currentAssessment.riveUrl);
          
        }
        
        setTimeout(() => {
          updateZoomFactor();
        }, 500);

        setIsConfigLoaded(true);
        setFeedbackMessage(currentConfig.current.manualInstruction);
       }catch(error){
        console.error('Error:', error);
       }
      }
    };

    

    window.addEventListener('message', handleMessage);
    document.addEventListener('message', handleMessage);
    //window.parent.postMessage({ type: 'READY_FOR_CONFIG' }, '*');
    
    //will comment out in production
    // setTimeout(() => {
    //   window.parent.postMessage({
    //     type: 'SCAN_CONFIG',
    //     scanConfigs: [
    //       {
    //         id: "as_eys_01",
    //         configs: [
    //           {
    //             id: "left_eye",
    //             name: "Left Eye",
    //             position: "front",
    //             zoomFactor: 2,
    //             extractRegion: "left_eye"
    //           },
    //           {
    //             id: "right_eye",
    //             name: "Right Eye",
    //             position: "front",
    //             zoomFactor: 2,
    //             extractRegion: "right_eye"
    //           }
    //         ]
    //       },
    //       // {
    //       //   id: "as_hair_02",
    //       //   riveUrl: "https://firebasestorage.googleapis.com/v0/b/rootallyai.appspot.com/o/reactScans%2Fhair%2Fhair_assessment_calib.riv?alt=media&token=8e0f1ece-d980-4734-83c2-d15bb1646b3c",
    //       //   configs: [
    //       //     {
    //       //       id: "front_face",
    //       //       name: "Front Face",
    //       //       position: "front",
    //       //       extractRegion: null,
    //       //       zoomFactor: 1,
    //       //       manualInstruction: "Please position your face within the guide"
    //       //     },
    //       //     {
    //       //       id: "left_face",
    //       //       name: "Left Side",
    //       //       position: "left",
    //       //       extractRegion: null,
    //       //       zoomFactor: 1,
    //       //       manualInstruction: "Please look all the way to the left"
    //       //     },
    //       //     {
    //       //       id: "right_face",
    //       //       name: "Right Side",
    //       //       position: "right",
    //       //       extractRegion: null,
    //       //       zoomFactor: 1,
    //       //       manualInstruction: "Please look all the way to the right"
    //       //     },
    //       //     {
    //       //       id: "head_down",
    //       //       name: "Head Down",
    //       //       position: "down",
    //       //       extractRegion: null,
    //       //       zoomFactor: 1,
    //       //       manualInstruction: "Please tilt your head down"
    //       //     }
    //       //   ]
    //       // }
    //     ]
    //   }, '*');
    // }, 5000);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []); // Empty dependency array - runs once on mount

  // Second useEffect for camera initialization
  useEffect(() => {
    if (isConfigLoaded && videoRef.current) {
      console.log('Starting camera after config loaded');
      //startCamera();
    }

    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }

      if (cameraRef.current) {
        console.log('Cleaning up camera');
        cameraRef.current.stop();
      }
    };
  }, [isConfigLoaded]); // Runs when isConfigLoaded changes

  // Initialize audio manager when component mounts
  useEffect(() => {
    AudioManager.init();
  }, []);

  // Rive animation
  const { RiveComponent, rive } = useRive({
    src: currentRiveAnimation,
    stateMachines: 'State Machine 1',
    layout: new Layout({
      fit: Fit.Cover,
      alignment: Alignment.Center,
    }),
    autoplay: true,
    onLoad: () => {
      console.log('Rive animation loaded');
      
    },
    onError: (err) => {
      console.error('Rive animation error:', err);
    }
  });

  // State machine inputs
  const positionInput = useStateMachineInput(rive, 'State Machine 1', 'Position');

  // Effect to handle position changes
  useEffect(() => {
    if (!rive || !positionInput) {
      console.log('Rive or positionInput not ready');
      return;
    }

    console.log('Setting up position input with initial value');
    positionInput.value = 0;
    positionInput.fire();
  }, [rive, positionInput]);

  const triggerPosition = (value) => {
    if (!positionInput) {
      console.log('Position input not available');
      return;
    }
    console.log('Triggering position:', value);
    positionInput.value = value;
    positionInput.fire();
  };

  // // Handle window resize
  // useEffect(() => {
  //   const handleResize = () => {
  //     //updateCameraDimensions();
  //     setDimensions({
  //       width: CAMERA_CONFIG.WIDTH,
  //       height: CAMERA_CONFIG.HEIGHT
  //     });

  //     // Update canvas dimensions
  //     if (canvasRef.current) {
  //       canvasRef.current.width = CAMERA_CONFIG.WIDTH;
  //       canvasRef.current.height = CAMERA_CONFIG.HEIGHT;
  //     }
  //     if (objectCanvasRef.current) {
  //       objectCanvasRef.current.width = CAMERA_CONFIG.WIDTH;
  //       objectCanvasRef.current.height = CAMERA_CONFIG.HEIGHT;
  //     }
  //   };

  //   window.addEventListener('resize', handleResize);
  //   handleResize(); // Initial setup

  //   return () => window.removeEventListener('resize', handleResize);
  // }, []);

  const getStatusColor = (condition) => condition ? '#00ff00' : '#ff0000';
  const getStatusText = (condition) => condition ? '✅' : '❌';

  const getErrorMessage = () => {
    if (!faceDetected) return "No face detected";
    if (!faceSizeOk) return "Please adjust your distance from camera";
    if (!facePositionOk) return "Please center your face within the guide";
    if (!faceTiltOk) return "Please keep your head straight";
    if (!eyesInGuideOk) return "Please position both eyes within the guide";
    if (!eyesOpen) return "Please open your eyes wider";
    return "";
  };

  const getInstructionMessage = () => {
    if (eyesCaptured) return "Eyes captured successfully!";
    if (isCountingDown) return `Open your eyes wide and don't move! Capturing in ${Math.ceil(countdownTime)}...`;
    if (getErrorMessage()) return "";
    return "Please maintain proper position";
  };

  if (!isConfigLoaded || !scanConfigs) {
    return <LoadingScreen />;
  }

  return (
    <div className="app-container">
      {/* Full-screen camera preview */}
      <div className="camera-container">
        {/* <video
          ref={videoRef}
          className="video-preview"
          autoPlay
          muted
          playsInline
          style={{
            width: dimensions.width,
            height: dimensions.height,
          }}
        /> */}
        <Camera ref={videoRef} />
        {/* <canvas
          ref={canvasRef}
          className="canvas-overlay"
          width={dimensions.width}
          height={dimensions.height}
          style={{
            width: dimensions.width,
            height: dimensions.height
          }}
        /> */}
      </div>

      {/* Rectangular face guide with dashed border */}
      <div className="face-guide-container">
        <div className="capture-button-container">
          <button className="capture-button">
            {feedbackMessage}
          </button>
        </div>
        <div className="face-guide-rectangle">
        {currentRiveAnimation && (
            <div className="rive-container">
              <RiveComponent />
            </div>
          )}
        </div>
      </div>

      {/* Top instruction area */}
      <div className="top-instruction-area">
        <div className="instruction-text">
          {isCountingDown 
            ? `Hold the position! Capturing in ${Math.ceil(countdownTime)}...`
            : ""}
        </div>
      </div>

      {/* Eye icon */}
      {/* <div className="eye-icon">
        <svg width="108" height="90" viewBox="0 0 108 90" fill="none">
          <path d="M54 22.5C32.4 22.5 14.4 36 7.2 54C14.4 72 32.4 85.5 54 85.5C75.6 85.5 93.6 72 100.8 54C93.6 36 75.6 22.5 54 22.5ZM54 72C43.2 72 34.2 63 34.2 54C34.2 45 43.2 36 54 36C64.8 36 73.8 45 73.8 54C73.8 63 64.8 72 54 72ZM54 45C48.6 45 44.1 49.5 44.1 54C44.1 58.5 48.6 63 54 63C59.4 63 63.9 58.5 63.9 54C63.9 49.5 59.4 45 54 45Z" fill="rgba(255, 255, 255, 0.54)"/>
        </svg>
      </div> */}

      {/* Bottom controls area */}
      <div className="bottom-controls-area">
        {errorMessage && (
          <div className="error-message">{errorMessage}</div>
        )}
        
        
          <div className="capture-controls">
            <div className="progress-bar-container">
              {scanConfigs.current && scanConfigs.current[assessmentIndex.current].configs.map((config, index) => (
                <div 
                  key={index} 
                  className={`progress-segment ${
                    index === currentConfigIndex.current ? 'current' : 
                    index < currentConfigIndex.current ? 'completed' : ''
                  }`}
                >
                  {index < currentConfigIndex.current && (
                    <svg className="tick-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4.89163 13.2687L9.16582 17.5427L18.7085 8" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                  {config.name}
                </div>
              ))}
            </div>
            <div className="capture-controls-row">
              <button 
                className="manual-capture-button"
                onClick={() => {
                  const video = document.querySelector("video");
                  if (!video || !video.srcObject) {
                    console.log("Video element or stream not ready");
                    return;
                  }

                  // Get video dimensions
                  const videoWidth = video.videoWidth;
                  const videoHeight = video.videoHeight;

                  const videoRect = video.getBoundingClientRect();
                  console.log("videoRect:", videoRect);

                  const videoPreviewWidth = videoRect.width;
                  const videoPreviewHeight = videoRect.height;

                  const previewRatioWidth = videoPreviewWidth / videoWidth;
                  const previewRatioHeight = videoPreviewHeight / videoHeight;

                  console.log("previewRatioWidth:", previewRatioWidth, "previewRatioHeight:", previewRatioHeight);
                  
                  console.log("videoWidth:", videoWidth, "videoHeight:", videoHeight);
                  
                  // Check if video is zoomed
                  const videoElement = document.querySelector("video");
                  const isZoomed = videoElement && videoElement.classList.contains("zoomed");
                  const zoomFactor = isZoomed ? 2 : 1;
                  
                  console.log("isZoomed:", isZoomed, "zoomFactor:", zoomFactor);
                  
                  // Get the face guide rectangle dimensions (fixed at 360x432)
                  
                  const guideRect = document.querySelector(".face-guide-rectangle").getBoundingClientRect();
                  
                  const guideWidth = guideRect.width;
                  const guideHeight = guideRect.height;

                  console.log("guideRect:", guideRect);
                  //setFeedbackMessage(guideRect.width + "x" + guideRect.height);
                  //console.log("guideRectWidth:", guideRectWidth, "guideRectHeight:", guideRectHeight);

                  // Calculate effective video dimensions when zoomed
                  const effectiveVideoWidth = videoWidth / zoomFactor;
                  const effectiveVideoHeight = videoHeight / zoomFactor;
                  
                  // Calculate the center position based on effective dimensions
                  let sourceX, sourceY, sourceWidth, sourceHeight;
                  
                  if (isZoomed) {
                    // object-fit: cover picks the axis that needs more scaling
                    const coverScale = Math.max(previewRatioWidth, previewRatioHeight);

                    // Where native video content starts inside the 2× CSS element
                    // (negative when content is wider/taller than the element)
                    const contentOffsetX = (videoRect.width - videoWidth * coverScale) / 2;
                    const contentOffsetY = (videoRect.height - videoHeight * coverScale) / 2;

                    // Front camera is horizontally mirrored: screen-left = native-right.
                    // Map guide's right screen edge → native sourceX so the crop stays aligned
                    // regardless of device screen size or camera resolution.
                    const videoRectRight = videoRect.left + videoRect.width;
                    sourceX = (videoRectRight - guideRect.right - contentOffsetX) / coverScale;
                    sourceY = (guideRect.top - videoRect.top - contentOffsetY) / coverScale;
                    sourceWidth = guideRect.width / coverScale;
                    sourceHeight = guideRect.height / coverScale;

                  } else {
                    // Normal capture - same as before
                    sourceWidth = guideWidth;
                    sourceHeight = guideHeight;
                    sourceX = (videoWidth - sourceWidth) / 2;
                    sourceY = (videoHeight - sourceHeight) / 2;
                  }

                  //sourceX = 225.7;
                  //sourceWidth = 77.2;
                  //sourceY = 78.5;
                  //sourceHeight = 92.7;
                  
                  

                  console.log("Source rectangle:", {
                    sourceX: sourceX, sourceY: sourceY, sourceWidth: sourceWidth, sourceHeight: sourceHeight,
                    effectiveVideoWidth: effectiveVideoWidth/previewRatioWidth, effectiveVideoHeight: effectiveVideoHeight/previewRatioHeight
                  });
                  
                  // Draw the cropped region to objectCanvas
                  const ctx = objectCanvasRef.current.getContext('2d');
                  
                  // Set the objectCanvas dimensions to match the guide rectangle
                  objectCanvasRef.current.width = guideWidth;
                  objectCanvasRef.current.height = guideHeight;
                  
                  // Clear and draw the cropped region
                  ctx.clearRect(0, 0, guideWidth, guideHeight);
                  ctx.drawImage(
                    video,
                    sourceX, sourceY, sourceWidth, sourceHeight, // Source rectangle (adjusted for zoom)
                    0, 0, guideWidth, guideHeight                // Destination rectangle
                  );
                  
                  // Set captured state immediately to prevent multiple calls
                  setEyesCaptured(true);
                  setIsCountingDown(false);
                  
                  setCurrentConfigLabel(currentConfig.current.name);
                  
                  // Save the cropped image
                  setCapturedObject(objectCanvasRef.current.toDataURL());
                  
                  // Stop camera
                  if (cameraRef.current) {
                    cameraRef.current.stop();
                  }
                }}
              >
                <svg width="43" height="39" viewBox="0 0 43 39" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21.6898 27.532C24.9504 27.532 27.5936 24.8954 27.5936 21.643C27.5936 18.3905 24.9504 15.7539 21.6898 15.7539C18.4293 15.7539 15.7861 18.3905 15.7861 21.643C15.7861 24.8954 18.4293 27.532 21.6898 27.532Z" stroke="white" strokeWidth="3"/>
                  <path d="M17.3171 37.3452H26.0625C32.2043 37.3452 35.2762 37.3452 37.4822 35.9024C38.4341 35.28 39.2539 34.477 39.8949 33.5389C41.3688 31.3796 41.3688 28.3703 41.3688 22.3556C41.3688 16.3389 41.3688 13.3316 39.8949 11.1722C39.2539 10.2341 38.4341 9.43105 37.4822 8.80877C36.0653 7.88026 34.2903 7.54851 31.5726 7.43073C30.2757 7.43073 29.1599 6.46885 28.9061 5.22233C28.7121 4.30927 28.208 3.49102 27.4791 2.90587C26.7501 2.32073 25.841 2.00457 24.9053 2.01083H18.4742C16.5299 2.01083 14.8553 3.3555 14.4735 5.22233C14.2196 6.46885 13.1038 7.43073 11.807 7.43073C9.09126 7.54851 7.31621 7.88222 5.89735 8.80877C4.9461 9.43117 4.12698 10.2343 3.48667 11.1722C2.01074 13.3316 2.01074 16.3389 2.01074 22.3556C2.01074 28.3703 2.01074 31.3776 3.4847 33.5389C4.1223 34.4733 4.94095 35.2762 5.89735 35.9024C8.10337 37.3452 11.1753 37.3452 17.3171 37.3452Z" stroke="white" strokeWidth="3"/>
                  <path d="M35.465 15.7539H33.4971" stroke="white" strokeWidth="3" strokeLinecap="round"/>
                </svg>
              </button>
              
              <button className="zoom-button" onClick={() => {
                const videoClass = document.querySelector("video");
                if (videoClass) {
                  if(videoClass.classList.contains("zoomed")){
                    videoClass.classList.remove("zoomed");
                  }else{
                    videoClass.classList.add("zoomed");
                  }
                }
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 15V18C4 19.1046 4.89543 20 6 20H9M15.2173 20H18C19.1046 20 20 19.1046 20 18V15M20 9V6C20 4.89543 19.1046 4 18 4H15M4 9V6C4 4.89543 4.89543 4 6 4H9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
             
            </div>
            {/* <div className="capture-button-container">
              <button className="capture-button">
                {feedbackMessage}
              </button>
            </div> */}
          </div>
       
      </div>

      {/* Hidden canvases for eye extraction */}
      <canvas
        ref={leftEyeCanvasRef}
        className="eye-canvas"
        width={200}
        height={100}
        style={{ display: 'none' }}
      />
      <canvas
        ref={rightEyeCanvasRef}
        className="eye-canvas"
        width={200}
        height={100}
        style={{ display: 'none' }}
      />
      <canvas
        ref={objectCanvasRef}
        className="object-canvas"
        width={dimensions.width}
        height={dimensions.height}
        style={{ display: 'none' }}
      />

      {/* Review Screen */}
      {eyesCaptured && (
        <div className="review-screen">
          <div className="review-header">
            <h2>Review Your Capture</h2>
          </div>
          
          <div className="eye-images-container">
            <div className="eye-image-wrapper">
              <h3>{currentConfigLabel}</h3>
              {capturedObject && (
                <img src={capturedObject} alt="Left Eye" className="captured-eye-image" />
              )}
            </div>
            
            {/* <div className="eye-image-wrapper">
              <h3>Right Eye</h3>
              {capturedRightEye && (
                <img src={capturedRightEye} alt="Right Eye" className="captured-eye-image" />
              )}
            </div> */}
          </div>
          
          <div className="review-buttons">
            <button className="retake-button" onClick={() => {
              // Open base64 images in new tabs for testing
              // if (capturedObject) {
              //   console.log('capturedObject:', capturedObject);
              //   openBase64InNewTab(capturedObject, 'image/jpeg');
              // }

              // Stop camera
              if (cameraRef.current) {
                cameraRef.current.start();
              }

              setEyesCaptured(false);
              setCapturedLeftEye(null);
              setCapturedRightEye(null);
              setCapturedObject(null);
              setIsCountingDown(false);
              setCountdownTime(CAPTURE_COUNTDOWN_TIME);
              setInstructionMessage("");
              setErrorMessage("");
            }}>
              Retake
            </button>
            
            <button className="proceed-button" onClick={() => {
              // Handle proceed action
              console.log('Proceeding with captured images');
              
              const canMoveToNextConfig = moveToNextConfig();
              if (canMoveToNextConfig) {
                // Stop camera
                if (cameraRef.current) {
                  cameraRef.current.start();
                }

                setEyesCaptured(false);
                setCapturedObject(null);
                setIsCountingDown(false);
                setCountdownTime(CAPTURE_COUNTDOWN_TIME);
                setInstructionMessage("");
                setErrorMessage("");
              }else{

                // const video = document.querySelector("video");
                // if (video) {
                //   video.srcObject = null;
                // }
                //stream.current.getTracks().forEach(track => track.stop());
                
                //currentConfig = scanConfigs.configs[0];
                // Prepare the data to send back to Flutter
                  const responseData = {
                    type: 'SCAN_COMPLETE',
                    scanConfigs: scanConfigs.current
                  };

                  // Try WebViewBridge first for native platforms
                  // WebViewBridge.sendResult(createWebViewEvent(WebViewEvents.SCAN_COMPLETE, {
                  //   module: 'eye-scan',
                  //   data: responseData.data
                  // }));

                  // Also send direct postMessage for Flutter web
                  //window.parent.postMessage(responseData, '*');

                  // Send scan data to Flutter environment through postMessage
                if (window.flutter_inappwebview) {
                  console.log("sending flutter event handler postmessage");
                  window.flutter_inappwebview.callHandler("data", responseData);
                }else{
                  console.log("sending non flutter webviewevent handler postmessage");
                  // Use flutter_inappwebview handler instead of parent
                  
                  window.parent.postMessage(JSON.stringify(responseData),"*");
                }
              }
              
              

            }}>
              Proceed
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default App; 